<template lang="pug">
.inspection-reservation-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：受付
    template(#cell(approved)="row")
      ApprovedSwitch(v-if="row.item.datetime" v-model="row.item.approved" @onSwitch="onChangeApproved(row.item)")
      .no-datetime.warning-message(v-else)
        div 未受付
        div 見学日時未定
    // セル：受付
    template(#cell(applicationType)="row")
      span {{ convertApplicationType(row.item.applicationType) }}
    // セル：申込み日時
    template(#cell(applicationDatetime)="row")
      span {{ convertDate(row.item.applicationDatetime) }}
      span （
      span(:class="getWeekClass(convertWeek(row.item.applicationDatetime))") {{ convertWeek(row.item.applicationDatetime) }}
      span ）
      span {{ convertTime(row.item.applicationDatetime) }}
    // セル：見学日時
    template(#cell(datetime)="row")
      .div(v-if="row.item.datetime")
        span {{ convertDate(row.item.datetime) }}
        span （
        span(:class="getWeekClass(convertWeek(row.item.datetime))") {{ convertWeek(row.item.datetime) }}
        span ）
        span {{ convertTime(row.item.datetime) }}
    // セル：氏名／電話番号
    template(#cell(nameAndTel)="row")
      .name {{ row.item.name }}
      .tel {{ row.item.tel }}
    // セル：申込者からの連絡事項
    template(#cell(applicantMessage)="row")
      .overflow-hidden.title-text(:id="`applicant-message-${row.item.created}`") {{ row.item.applicantMessage }}
      b-popover(v-if="row.item.applicantMessage" :target="`applicant-message-${row.item.created}`" triggers="hover")
        .d-flex.flex-column
          span {{ `${row.item.applicantMessage}` }}
    // セル：受付メモ
    template(#cell(receptionMemo)="row")
      .overflow-hidden.title-text(:id="`reception-memo-${row.item.created}`") {{ row.item.receptionMemo }}
      b-popover(v-if="row.item.receptionMemo" :target="`reception-memo-${row.item.created}`" triggers="hover")
        .d-flex.flex-column
          div {{ `${row.item.receptionMemo}` }}
    // セル：操作
    template(#cell(operation)="row")
      button.btn.edit-btn.mr-2(@click="onEdit(row.item)") 修正
      button.btn.delete-btn(@click="onDelete(row.item)" type="button") 削除
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { ApplicationType, InspectionReservationsGetModel } from "@/commons/services/InspectionReservations";

import ApprovedSwitch from "@/pc/basics/ApprovedSwitch.vue";

/**
 * ### 施設見学テーブルコンポーネント
 */
@Component({
  components: {
    ApprovedSwitch
  }
})
export default class InspectionReservationTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<InspectionReservationTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<InspectionReservationTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<InspectionReservationTableItem> {
    return this.value;
  }

  /**
   * ### 日時を「yyyy年mm月dd日」形式に変換する
   */
  public convertDate(datetime: number): string {
    const date: string = this.$dateUtil.convertNumberToString(datetime, "mm月dd日");
    return date;
  }

  /**
   * ### 日時から曜日文字列に変換する
   */
  public convertWeek(datetime: number): string {
    const week: string = this.$dateUtil.getWeekName(this.$dateUtil.convertNumberToDate(datetime), "ja");
    return week;
  }

  /**
   * ### 曜日に応じたCSSクラスを取得する
   */
  public getWeekClass(week: string): string {
    switch (week) {
      case "日": {
        return "sunday";
      }
      case "土": {
        return "saturday";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 日時を「hh：MM」形式に変換する
   */
  public convertTime(datetime: number): string {
    const time: string = this.$dateUtil.convertNumberToString(datetime, "hh：MM");
    return time;
  }

  /**
   * ### 申込み種別を文字列に変換する
   */
  public convertApplicationType(applicationType: ApplicationType): string {
    if (!applicationType) {
      return "";
    } else {
      switch (applicationType) {
        case ApplicationType.USER: {
          return "本人";
        }
        case ApplicationType.FACILITY: {
          return "代理";
        }
        default: {
          return "";
        }
      }
    }
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: InspectionReservationTableItem, filter: FilterSetting): boolean {
    return this.filterSearchWords(row, filter);
  }

  /**
   * ### 検索文字列によるフィルタリング
   */
  public filterSearchWords(row: InspectionReservationTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundName: boolean = true;
    let foundApplicantMessage: boolean = true;
    let foundReceptionMemo: boolean = true;

    for (const word of words) {
      if (row.name.indexOf(word) == -1) {
        foundName = false;
      }

      if (row.applicantMessage == null) {
        foundApplicantMessage = false;
      }
      if (row.applicantMessage != null && row.applicantMessage.indexOf(word) == -1) {
        foundApplicantMessage = false;
      }

      if (row.receptionMemo == null) {
        foundReceptionMemo = false;
      }
      if (row.receptionMemo != null && row.receptionMemo.indexOf(word) == -1) {
        foundReceptionMemo = false;
      }
    }

    return foundName || foundApplicantMessage || foundReceptionMemo;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 受付状態スイッチの押下イベント
   */
  public onChangeApproved(item: InspectionReservationTableItem): void {
    this.$emit("onChangeApproved", item);
    return;
  }

  /**
   * ### 修正ボタン押下イベント
   */
  public onEdit(item: InspectionReservationTableItem): void {
    this.$emit("onEdit", item);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: InspectionReservationTableItem): void {
    this.$emit("onDelete", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface InspectionReservationTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface InspectionReservationTableItem extends InspectionReservationsGetModel {}

export interface FilterSetting {
  searchWord: string;
}
</script>

<style lang="scss" scoped>
.inspection-reservation-table-component {
  .no-datetime {
    font-size: 14px;
  }

  // 文字がtd要素の幅をはみ出した場合「…」で隠すスタイル
  .overflow-hidden {
    overflow: hidden !important;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .edit-btn,
  .delete-btn {
    width: 64px;
  }
}
</style>
